// Main Plutoo - Client Configuration (Production Environment)
import { FEATURES } from '../features.js';

export const PLUTOO_CONFIG = {
  // Client identification
  clientName: 'plutoo',
  displayName: 'Plutoo Property Management',
  
  // Branding configuration
  branding: {
    companyName: 'Plutoo Property Management',
    shortName: 'Plutoo',
    logo: '/uploads/logos/plutoo-logo.png',
    logoAlt: 'Plutoo Property Management Logo',
    favicon: '/favicon.jpg',
    
    // Theme colors - classic professional
    primaryColor: '#6366F1',      // Indigo
    secondaryColor: '#8B5CF6',    // Purple
    accentColor: '#A78BFA',       // Light purple
    backgroundColor: '#F8FAFC',   // Subtle gray background
    textColor: '#1E293B',         // Dark slate text
    
    // Theme name for CSS classes
    theme: 'plutoo-theme',
    
    // Custom CSS variables
    customCss: {
      '--primary-color': '#6366F1',
      '--secondary-color': '#8B5CF6',
      '--accent-color': '#A78BFA',
      '--background-color': '#F8FAFC',
      '--text-color': '#1E293B',
    }
  },
  
  // Firebase configuration
  firebase: {
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    // Note: All Firebase configuration should be provided via environment variables
    // See ENVIRONMENT_VARIABLES_GUIDE.md for required variables
  },
  
  // Domain and deployment configuration
  deployment: {
    domain: process.env.REACT_APP_DEPLOYMENT_DOMAIN || 'dashboard.plutoo.co.uk',
    ftpPath: process.env.REACT_APP_FTP_PATH || '/public_html/dashboard',
    buildPath: process.env.REACT_APP_BUILD_PATH || './build-plutoo/',
  },
  
  // Client-specific feature overrides
  // Main Plutoo is conservative - only stable features enabled
  features: {
    // Core features (inherited from global config - all enabled)
    // Tenant Portal is now a core feature - always enabled
    
    // New features - very conservative rollout for main production
    [FEATURES.AUTOMATED_ALERTS]: false,       // Needs careful configuration
  },
  
  // Client-specific customizations
  customizations: {
    // UI/UX customizations - professional defaults
    showAdvancedFilters: false,  // Keep UI simple for main production
    maxPropertiesPerPage: 25,    // Conservative for performance
    enablePropertyGroups: false, // Not needed for all users
    showPropertyImages: true,
    defaultSortOrder: 'property_name',
    
    // Dashboard customizations - core widgets only
    dashboardWidgets: [
      'property_overview',
      'recent_maintenance',
      'rent_summary'
    ],
    
    // Navigation customizations
    sidebarCollapsed: false,
    showQuickActions: false,     // Keep navigation simple
    showBetaFeatures: false,     // No beta features in production
    
    // Financial settings
    currencySymbol: process.env.REACT_APP_CURRENCY_SYMBOL || '£',
    currencyCode: process.env.REACT_APP_CURRENCY_CODE || 'GBP',
    dateFormat: process.env.REACT_APP_DATE_FORMAT || 'DD/MM/YYYY',
    
    // Property management specific
    allowMultipleLeases: false,  // Keep simple for most users
    requirePropertyPhotos: true,
    autoGeneratePropertyCodes: false,  // Manual control for main
    propertyCodePrefix: process.env.REACT_APP_PROPERTY_CODE_PREFIX || 'PLT',
    
    // Tenant management
    requireTenantReferences: true,
    allowJointTenancies: false,  // Keep simple initially
    autoSendWelcomeEmails: false, // Manual control for main
    
    // Maintenance settings
    allowTenantMaintenanceRequests: false, // Feature for later
    requireMaintenancePhotos: true,
    autoAssignMaintenanceToPreferredContractors: false,
    
    // Notification preferences - conservative
    emailNotifications: {
      newTenant: false,          // Manual control
      maintenanceRequest: true,
      rentOverdue: true,
      certificateExpiry: true,
      newFeatureAnnouncements: false, // No spam
    },
    
    // Reporting preferences
    defaultReportPeriod: 'monthly',
    includeVATInReports: true,
    showProfitLossReports: false, // Advanced feature
    showAdvancedAnalytics: false, // Not ready yet
  },
  
  // Client-specific menu structure - core features only
  menuStructure: {
    dashboard: { enabled: true, order: 1 },
    properties: { enabled: true, order: 2 },
    tenants: { enabled: true, order: 3 },
    maintenance: { enabled: true, order: 4 },
    finance: { enabled: true, order: 5 },
    reports: { enabled: true, order: 6 },
    messages: { enabled: true, order: 7 },
    settings: { enabled: true, order: 8 },
    // analytics: { enabled: false },  // Future feature
    // help: { enabled: false },       // Not needed for main users yet
  },
  
  // Client contact information
  support: {
    email: process.env.REACT_APP_SUPPORT_EMAIL || 'support@plutoo.co.uk',
    phone: process.env.REACT_APP_SUPPORT_PHONE || '+44 (0) 203 393 8001',
    hours: process.env.REACT_APP_SUPPORT_HOURS || 'Monday - Friday, 9:00 AM - 6:00 PM GMT',
  },
  
  // Terms and compliance
  legal: {
    termsOfService: '/terms',
    privacyPolicy: '/privacy',
    dataRetentionPeriod: '7 years',
    gdprCompliant: true,
  },
  
  // Production-specific configuration
  production: {
    enableAnalytics: true,
    enableErrorReporting: true,
    enablePerformanceMonitoring: true,
    strictSecurity: true,
    requireHttps: true,
  }
}; 